import { P2GBox } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { P2GAlert } from '@parcel2go/core-ui';
import { P2GFade } from '@parcel2go/core-ui';
import { useContext, useEffect, useState } from 'react';
import {
  ActionType,
  GetInstructionsApiResponseModel,
  IntegrationType,
  StrVal
} from '../../../lib/api/account-linking/types';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GDivider } from '@parcel2go/core-ui';
import Steps from './Steps';
import { P2GProgress } from '@parcel2go/core-ui';
import { SumNumber } from '../lib/helpers/Array';
import InstructionsFooter from './InstructionsFooter';
import { ValidationErrorMessages } from '../lib/helpers/ValidateIntegration';
import { NotificationContext } from '../lib/contexts/NotificationContext';
import { AccountActionResponseHelper } from '../lib/helpers/AccountLinkingResponseHelper';
import { useAccountLinkingApi } from '~/lib/api/account-linking/use-account-linking-api';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { isLinkOrInstallAction } from '~/apps/smartsend/components/onboarding/components/is-link-or-install-action';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { EventSections } from '~/apps/smartsend/lib/events/EventLogger';
import { IsBetaIntegration } from '../constants/beta-integrations';
import BetaIntegrationFeedback from './BetaIntegrationFeedback';
import Verify from './Verify';

interface props {
  integrationSlug: string;
  integrationType: IntegrationType;
  onComplete: (accountId: string, followUpAction?: ActionType) => void;
  action: ActionType;
  accountId: string;
  configRequired: boolean;
  onError?: () => void;
  onInstructionsLoaded?: (instructions: GetInstructionsApiResponseModel) => void;
  hideFooter?: boolean;
}

export default function ActionInstructions(props: props) {
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.ACCOUNT_LINKING);
  const { getActionInstructions, postActionValues } = useAccountLinkingApi();
  const notificationContext = useContext(NotificationContext);
  const [instructions, setInstructions] = useState<GetInstructionsApiResponseModel>(null);
  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [canGetActions, setCanGetActions] = useState<boolean>(false);
  const [hasInputs, setHasInputs] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const isBetaIntegration = IsBetaIntegration(props.integrationSlug);

  useEffect(() => {
    setLoading(true);

    if (!props.action) {
      setInstructions(null);
      return;
    }

    getActionInstructions(props.integrationType, props.integrationSlug, props.action, props.accountId).then((r) => {
      if (!r.success && props.onError) {
        props.onError();
      }
      setCanGetActions(r.success);
      setInstructions(r.result);
      if (props.onInstructionsLoaded) {
        props.onInstructionsLoaded(r.result);
      }
      if (r.success && r.result.showInstructions) {
        setShowInstructions(true);
      }
      setHasInputs(SumNumber(r.result.steps.map((x) => x.inputs?.length || 0)) > 0);
      setLoading(false);
    });
  }, [props.integrationType, props.integrationSlug, props.action, props.accountId]);

  const onSubmit = (values: StrVal) => {
    if (instructions?.readonly === true) {
      return;
    }

    setSaving(true);
    setErrorMessages([]);
    postActionValues(props.integrationType, props.integrationSlug, props.action, values, props.accountId).then((r) => {
      if (r.success) {
        if (r.result.redirectUrl) {
          document.location.href = r.result.redirectUrl;
        } else {
          notificationContext.dispatch({
            type: 'ADD',
            payload: AccountActionResponseHelper(
              props.action,
              props.integrationType,
              props.integrationSlug,
              r.success,
              props.accountId,
              r.result.accountStatus === 'ConfigurationRequired',
              t
            )
          });

          let followUpAction: ActionType = r.result.accountStatus === 'ConfigurationRequired' ? 'Configure' : null;
          if (props.integrationType === 'KnowYourCustomer' && r.result.accountStatus === 'Pending') {
            followUpAction = 'Verify';
          }

          props.onComplete(r.result.accountId, followUpAction);

          // Event for Account Linked (Only for accounts linkable via this flow)
          // Specific event for GA4, dont amend the type.
          if (props.integrationType === 'Marketplace' && isLinkOrInstallAction(props.action)) {
            track({
              type: 'ss_integration_success',
              payload: {
                ...GenerateBaseAnalyticsPayload([]),
                customerId: props.accountId || r?.result?.accountId,
                ss_source: props.integrationSlug
              }
            });
          }

          // Generic event for other analytics platforms
          track({
            type: 'account_action_completed',
            payload: GenerateBaseAnalyticsPayload([EventSections.MyAccount, EventSections.MarketplaceAccounts], {
              surface: 'account-linking',
              integrationType: props.integrationType,
              integrationSlug: props.integrationSlug,
              action: props.action
            })
          });
        }
      } else {
        setErrorMessages(ValidationErrorMessages(r.validationErrors, r.error?.code, props.integrationSlug, t));
        setSaving(false);
      }
    });
  };

  if (!props?.action) {
    return null;
  }

  if (loading) {
    return (
      <P2GBox textAlign="center" p={4}>
        <P2GProgress />
      </P2GBox>
    );
  }

  if (!canGetActions) {
    return (
      <P2GBox>
        <P2GText>{t('ACTION_UNAVAILABLE', { defaultValue: 'This action is unavailable.' })} </P2GText>
      </P2GBox>
    );
  }

  if (props.action === 'Verify') {
    return (
      <Verify
        onAttempt={async () => {
          const r = await postActionValues(
            props.integrationType,
            props.integrationSlug,
            props.action,
            {},
            props.accountId
          );
          if (!r.success) {
            setErrorMessages(ValidationErrorMessages(r.validationErrors, r.error?.code));
            return;
          }
          props.onComplete(r.result.accountId, r.result.accountStatus === 'ConfigurationRequired' ? 'Configure' : null);
        }}
      />
    );
  }

  return (
    <P2GFade in>
      <P2GBox key={`${props.integrationSlug}_${props.accountId}_${props.action}`}>
        {instructions.pleaseNoteText && (
          <P2GAlert color="info">
            <P2GText component="p">
              {t(`${props.integrationSlug}_${props.action}_PLEASE_NOTE`.toUpperCase(), {
                defaultValue: instructions.pleaseNoteText
              })}
            </P2GText>
          </P2GAlert>
        )}
        <P2GGrid container spacing={2} direction="column">
          {props.configRequired ? (
            <P2GGrid item>
              <P2GAlert color="info">
                <P2GText component="p">
                  {t('CONFIG_REQUIRED', {
                    defaultValue:
                      'Your account has been created, but there are some further settings you need to complete.'
                  })}
                </P2GText>
              </P2GAlert>
            </P2GGrid>
          ) : (
            instructions.headerText && (
              <P2GGrid item>
                <P2GText component="p">
                  {t(`${props.integrationSlug}_INSTRUCTIONS_${props.action}_HEADER`.toUpperCase(), {
                    defaultValue: instructions.headerText
                  })}
                </P2GText>
              </P2GGrid>
            )
          )}
          {hasInputs && (
            <P2GGrid item>
              <P2GBox pt={1} p={1}>
                <P2GText component="p">
                  {showInstructions ? (
                    <Trans
                      t={t}
                      i18nKey={`TURN_INSTRUCTIONS_OFF`}
                      defaults="<a>Turn instructions off</a>."
                      components={{ a: <P2GLink onClick={() => setShowInstructions(false)} /> }}
                    />
                  ) : (
                    <Trans
                      t={t}
                      i18nKey={`TURN_INSTRUCTIONS_ON`}
                      defaults="Need help? <a>View the instructions</a>."
                      components={{ a: <P2GLink onClick={() => setShowInstructions(true)} /> }}
                    />
                  )}
                </P2GText>
              </P2GBox>
            </P2GGrid>
          )}
          <P2GGrid item>
            <Steps
              hasInputs={hasInputs}
              integrationType={props.integrationType}
              integrationSlug={props.integrationSlug}
              action={props.action}
              steps={instructions.steps}
              terms={instructions.termsAndConditions}
              showInstructions={showInstructions}
              saving={saving}
              readonly={instructions.readonly}
              onSubmit={onSubmit}
            />
          </P2GGrid>
          {isBetaIntegration && (
            <P2GGrid item>
              <BetaIntegrationFeedback
                integrationSlug={props.integrationSlug}
                integrationType={props.integrationType}
                action={props.action}
                accountId={props.accountId}
              />
            </P2GGrid>
          )}
          {errorMessages.length > 0 && (
            <P2GGrid item>
              <P2GAlert color="error">
                {errorMessages.map((e) => (
                  <P2GText component="p" key={e}>
                    {e}
                  </P2GText>
                ))}
              </P2GAlert>
            </P2GGrid>
          )}
          {!props.hideFooter && instructions.footerText && (
            <>
              <P2GGrid item>
                <P2GDivider />
              </P2GGrid>
              <P2GGrid item>
                <InstructionsFooter
                  children={t(`${props.integrationSlug}_INSTRUCTIONS_${props.action}_FOOTER`.toUpperCase(), {
                    defaultValue: instructions.footerText
                  })}
                />
              </P2GGrid>
            </>
          )}
        </P2GGrid>
      </P2GBox>
    </P2GFade>
  );
}
